import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

interface NavbarProps {
  isHome?: boolean;
}

const navLinks = [
  { label: "首页", to: "/" },
  { label: "漫画分类", to: "/category/love" },
  { label: "最新更新", to: "/category/daily" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar({ isHome = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const solid = scrolled || !isHome || menuOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "bg-background-50/90 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 md:h-20 items-center justify-between px-4 md:px-6 lg:px-10">
        <Link to="/" aria-label="178漫画网首页" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-book-fill text-lg"></i>
          </span>
          {isHome ? (
            <h1 className="font-heading text-xl md:text-2xl text-foreground-950">
              178<span className="text-primary-600">漫画网</span>
            </h1>
          ) : (
            <span className="font-heading text-xl md:text-2xl text-foreground-950">
              178<span className="text-primary-600">漫画网</span>
            </span>
          )}
        </Link>

        <ul className="hidden md:flex items-center gap-1 lg:gap-2">
          {navLinks.map((l) => {
            const active =
              l.to === "/" ? pathname === "/" : l.to.startsWith("/category") ? pathname.startsWith("/category") : pathname.startsWith(l.to);
            return (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className={`inline-block whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    active
                      ? "bg-primary-100 text-primary-700 font-semibold"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`}
                >
                  {l.label}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (search.trim()) window.REACT_APP_NAVIGATE("/category/love");
            }}
            className="flex items-center gap-2 rounded-full bg-background-100 px-3 py-2"
          >
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="text"
              placeholder="搜索漫画…"
              className="w-32 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
          </form>
          <Link
            to="/category/love"
            className="inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-fire-line"></i>热门漫画
          </Link>
        </div>

        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-800"
          aria-label="打开菜单"
        >
          <i className={`ri-${menuOpen ? "close" : "menu"}-line text-xl`}></i>
        </button>
      </nav>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50/95 px-4 pb-5 pt-3 backdrop-blur-md">
          <div className="mb-3 flex items-center gap-2 rounded-full bg-background-100 px-3 py-2.5">
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="text"
              placeholder="搜索漫画…"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
          </div>
          <ul className="flex flex-col gap-1">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className={`block rounded-lg px-3 py-2.5 text-sm ${
                    pathname.startsWith(l.to === "/" ? "/" : l.to) ? "bg-primary-100 text-primary-700 font-semibold" : "text-foreground-700"
                  }`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}